/* eslint-disable import/no-cycle */
import React, { memo, useEffect, useRef } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionToggle,
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
} from '@strapi/design-system';
import { useCMEditViewDataManager } from '@strapi/helper-plugin';
import { Drag, Trash } from '@strapi/icons';
import get from 'lodash/get';
import toString from 'lodash/toString';
import PropTypes from 'prop-types';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useDragAndDrop } from '../../../hooks/useDragAndDrop';
import useLazyComponents from '../../../hooks/useLazyComponents';
import { composeRefs, getTrad, ItemTypes } from '../../../utils';
import FieldComponent from '../../FieldComponent';
import Inputs from '../../Inputs';

import Preview from './Preview';

const CustomIconButton = styled(IconButton)`
  background-color: transparent;

  svg {
    path {
      fill: ${({ theme, expanded }) =>
        expanded ? theme.colors.primary600 : theme.colors.neutral600};
    }
  }

  &:hover {
    svg {
      path {
        fill: ${({ theme }) => theme.colors.primary600};
      }
    }
  }
`;

const ActionsFlex = styled(Flex)`
  & .drag-handle {
    background: unset;

    svg {
      path {
        fill: ${({ theme, expanded }) => (expanded ? theme.colors.primary600 : undefined)};
      }
    }

    &:hover {
      svg {
        path {
          /* keeps the hover style of the accordion */
          fill: ${({ theme }) => theme.colors.primary600};
        }
      }
    }
  }
`;

// Issues:
// https://github.com/react-dnd/react-dnd/issues/1368
// https://github.com/frontend-collective/react-sortable-tree/issues/490

const DraggedItem = ({
  componentFieldName,
  componentUid,
  fields,
  index,
  isOpen,
  isReadOnly,
  mainField,
  moveComponentField,
  onClickToggle,
  toggleCollapses,
  onGrabItem,
  onDropItem,
  onCancel,
}) => {
  const { modifiedData, removeRepeatableField, triggerFormValidation } = useCMEditViewDataManager();

  const displayedValue = toString(
    get(modifiedData, [...componentFieldName.split('.'), mainField], '')
  );
  const accordionRef = useRef(null);
  const { formatMessage } = useIntl();

  /**
   * The last item in the fieldName array will be the index of this component.
   * Drag and drop should be isolated to the parent component so nested repeatable
   * components are not affected by the drag and drop of the parent component in
   * their own re-ordering context.
   */
  const componentKey = componentFieldName.split('.').slice(0, -1).join('.');

  const [{ handlerId, isDragging, handleKeyDown }, boxRef, dropRef, dragRef, dragPreviewRef] =
    useDragAndDrop(!isReadOnly, {
      type: `${ItemTypes.COMPONENT}_${componentKey}`,
      index,
      item: {
        displayedValue,
      },
      onMoveItem: moveComponentField,
      onStart() {
        // Close all collapses
        toggleCollapses();
      },
      onEnd() {
        // Update the errors
        triggerFormValidation();
      },
      onGrabItem,
      onDropItem,
      onCancel,
    });

  useEffect(() => {
    dragPreviewRef(getEmptyImage(), { captureDraggingState: false });
  }, [dragPreviewRef, index]);

  const composedAccordionRefs = composeRefs(accordionRef, dragRef);
  const composedBoxRefs = composeRefs(boxRef, dropRef);

  const { lazyComponentStore } = useLazyComponents();

  return (
    <Box ref={composedBoxRefs}>
      {isDragging ? (
        <Preview />
      ) : (
        <Accordion expanded={isOpen} onToggle={onClickToggle} id={componentFieldName} size="S">
          <AccordionToggle
            action={
              isReadOnly ? null : (
                <ActionsFlex gap={0} expanded={isOpen}>
                  <CustomIconButton
                    expanded={isOpen}
                    noBorder
                    onClick={() => {
                      removeRepeatableField(componentFieldName);
                      toggleCollapses();
                    }}
                    label={formatMessage({
                      id: getTrad('containers.Edit.delete'),
                      defaultMessage: 'Delete',
                    })}
                    icon={<Trash />}
                  />
                  <IconButton
                    className="drag-handle"
                    ref={composedAccordionRefs}
                    forwardedAs="div"
                    role="button"
                    noBorder
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    data-handler-id={handlerId}
                    label={formatMessage({
                      id: getTrad('components.DragHandle-label'),
                      defaultMessage: 'Drag',
                    })}
                    onKeyDown={handleKeyDown}
                  >
                    <Drag />
                  </IconButton>
                </ActionsFlex>
              )
            }
            title={displayedValue}
            togglePosition="left"
          />
          <AccordionContent>
            <Flex
              direction="column"
              alignItems="stretch"
              background="neutral100"
              padding={6}
              gap={6}
            >
              {fields.map((fieldRow, key) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid gap={4} key={key}>
                    {fieldRow.map(({ name, fieldSchema, metadatas, queryInfos, size }) => {
                      const isComponent = fieldSchema.type === 'component';
                      const keys = `${componentFieldName}.${name}`;

                      if (isComponent) {
                        const componentUid = fieldSchema.component;

                        return (
                          <GridItem col={size} s={12} xs={12} key={name}>
                            <FieldComponent
                              componentUid={componentUid}
                              intlLabel={{
                                id: metadatas.label,
                                defaultMessage: metadatas.label,
                              }}
                              isRepeatable={fieldSchema.repeatable}
                              isNested
                              name={keys}
                              max={fieldSchema.max}
                              min={fieldSchema.min}
                              required={fieldSchema.required}
                            />
                          </GridItem>
                        );
                      }

                      return (
                        <GridItem key={keys} col={size} s={12} xs={12}>
                          <Inputs
                            componentUid={componentUid}
                            fieldSchema={fieldSchema}
                            keys={keys}
                            metadatas={metadatas}
                            queryInfos={queryInfos}
                            size={size}
                            customFieldInputs={lazyComponentStore}
                          />
                        </GridItem>
                      );
                    })}
                  </Grid>
                );
              })}
            </Flex>
          </AccordionContent>
        </Accordion>
      )}
    </Box>
  );
};

DraggedItem.defaultProps = {
  componentUid: undefined,
  fields: [],
  isReadOnly: false,
  isOpen: false,
  onGrabItem: undefined,
  onDropItem: undefined,
  onCancel: undefined,
  toggleCollapses() {},
};

DraggedItem.propTypes = {
  componentFieldName: PropTypes.string.isRequired,
  componentUid: PropTypes.string,
  fields: PropTypes.array,
  index: PropTypes.number.isRequired,
  isOpen: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  mainField: PropTypes.string.isRequired,
  moveComponentField: PropTypes.func.isRequired,
  onGrabItem: PropTypes.func,
  onDropItem: PropTypes.func,
  onCancel: PropTypes.func,
  onClickToggle: PropTypes.func.isRequired,
  toggleCollapses: PropTypes.func,
};

export default memo(DraggedItem);
